import type { CareSeekerShallowLeadFormDataV2 } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { UrgencyType } from "../shared/form-options";
import type { HeroVariant } from "../shared/hero-variants";

import { track } from "@homeinstead/analytics";
import Quote from "@homeinstead/icons/Quote";
import { EventName, scrollToTop } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import { useFormContext } from "react-hook-form";
import { UrgencyOptions } from "../shared/form-options";
import { getHeroVariantStyles } from "../shared/hero-variants";
import { trackLegacyLeadFormStep } from "../shared/track-legacy-lead-form-step";
import { Testimony } from "../Testimony";
import { testimoniesAlternative } from "../testimonies";

interface UrgencyStepV4Props {
  className?: string;
  nextStep: () => void;
  variant?: HeroVariant;
}

const UrgencyStepV4 = ({
  className,
  nextStep,
  variant = "default",
}: UrgencyStepV4Props) => {
  const isV2FormVariant = true;
  const { setValue, watch } = useFormContext<CareSeekerShallowLeadFormDataV2>();
  const urgency = watch("urgency");

  const { textColorClass } = getHeroVariantStyles(variant);

  const handleUrgencySelect = (selectedUrgency: UrgencyType) => {
    setValue("urgency", selectedUrgency);

    track(EventName.TRACKED_CLICK, {
      event_type: "urgency_selection",
      form_variant: "v4",
      urgency: selectedUrgency,
    });
    trackLegacyLeadFormStep({
      formVariant: "v4",
      stepName: "urgency",
      userSelection: { urgency: selectedUrgency },
    });

    scrollToTop();
    nextStep();
  };

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col gap-4 gap-y-12 md:gap-y-4 xl:flex-row",
        className,
      )}
    >
      <input name="urgency" type="hidden" value={urgency || ""} />

      <div className="flex flex-col items-center gap-4 xl:items-start">
        <h1
          className={cn(
            "text-left font-bold font-jakarta md:text-center xl:text-left",
            "text-xl md:text-1xl",
            textColorClass,
          )}
        >
          How soon is care needed?
        </h1>
        <p
          className={cn(
            "w-full text-left font-bold md:max-w-[410px] md:text-center xl:text-left",
            textColorClass,
          )}
        >
          Whether it&apos;s an unexpected need or long-term care, we&apos;re
          here to provide flexible in-home support.
        </p>

        <div className="flex w-full flex-col gap-4 md:w-[440px]">
          {UrgencyOptions.map((option) => (
            <Button
              className="group flex h-12 w-full justify-start gap-4 rounded-full px-6 font-semibold"
              key={option.value}
              onClick={() => handleUrgencySelect(option.value)}
              type="button"
              variant="outline"
            >
              <span>{option.label}</span>
            </Button>
          ))}
        </div>
      </div>
      <Testimony
        className={cn(
          "mb-auto ml-auto hidden max-w-md flex-col items-end justify-end gap-4 xl:flex",
          {
            "max-w-[340px]": isV2FormVariant,
          },
        )}
        customIcon={<Quote />}
        variant={variant}
      >
        <div className="flex flex-col gap-4 text-end">
          {testimoniesAlternative.step5}
        </div>
      </Testimony>
    </div>
  );
};

export default UrgencyStepV4;
